import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Route, Routes, useParams, NavLink, useNavigate } from "react-router-dom";
import api, { formatApiError, getWsUrl } from "@/lib/api";
import logger from "@/lib/logger";
import SignaturePad from "@/components/SignaturePad";
import ClientIterationFeed from "@/components/ClientIterationFeed";
import { useAuth } from "@/context/AuthContext";
import { Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import AnalogyLogo from "@/components/AnalogyLogo";
import WorkingDayPicker, { workingDaysBetween } from "@/components/WorkingDayPicker";
import { projectStatusTone, milestoneStatusTone } from "@/lib/statusTone";
import ProjectUpdatesFeed from "@/components/ProjectUpdatesFeed";
import { renderRich } from "@/pages/Chat";
import { RelativeTime, ChatDateDivider, sameDay } from "@/lib/chatTime";
import { useChatTyping, TypingLine } from "@/lib/chatTyping";
import { RichChatComposer } from "@/components/ChatComposerKit";
import { LogOut, ChevronRight, ChevronDown, Building2, MessageSquare, Hash, Send, FileSignature, Plus, Target, AlertCircle, Sparkles, ArrowRight, FolderOpen, FileText, Package2, Download, X, Receipt, Eye, Quote, Cake, PartyPopper, Megaphone, CalendarHeart, Star, QrCode, AlertTriangle, Monitor, CheckCircle2, Bookmark, BookmarkCheck, Pin } from "lucide-react";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";
import { formatDate } from "@/lib/dateFormat";
import { toast } from "sonner";

function ClientShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen md:flex bg-[#F8F9FA]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 bg-[#0F172A] text-slate-200 flex-col">
        <div className="px-6 py-6 border-b border-white/10">
          <AnalogyLogo size={26} className="text-white" />
          <div className="label-overline text-slate-400 mt-2">Client portal</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          <NavLink to="/client" end className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
            <Building2 size={16} /> Projects
          </NavLink>
          <NavLink to="/client/chat" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
            <MessageSquare size={16} /> Project chat
          </NavLink>
          <NavLink to="/client/vault" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`} data-testid="nav-client-vault">
            <FolderOpen size={16} /> My Vault
          </NavLink>
        </nav>
        <div className="px-4 py-4 border-t border-white/10">
          <div className="text-sm font-medium text-white">{user?.name}</div>
          <div className="text-xs text-slate-400">{user?.title}</div>
          <button onClick={async () => { await logout(); nav("/login"); }} className="w-full mt-3 text-xs text-slate-200 hover:text-white inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-white/15 hover:bg-white/10" data-testid="client-logout-btn">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden bg-[#0F172A] text-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3 min-w-0">
          <AnalogyLogo size={22} className="text-white" />
          <div className="label-overline text-slate-400 truncate">Client portal</div>
        </div>
        <button onClick={async () => { await logout(); nav("/login"); }} className="text-xs text-slate-200 hover:text-white inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-white/15" data-testid="client-logout-btn-mobile">
          <LogOut size={12} /> Sign out
        </button>
      </header>
      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0F172A] text-slate-300 border-t border-white/10 grid grid-cols-3" data-testid="client-mobile-nav">
        <NavLink to="/client" end className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] ${isActive ? "text-white bg-white/10" : "text-slate-400"}`}>
          <Building2 size={16} /> Projects
        </NavLink>
        <NavLink to="/client/chat" className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] ${isActive ? "text-white bg-white/10" : "text-slate-400"}`}>
          <MessageSquare size={16} /> Chat
        </NavLink>
        <NavLink to="/client/vault" className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] ${isActive ? "text-white bg-white/10" : "text-slate-400"}`} data-testid="nav-client-vault-mobile">
          <FolderOpen size={16} /> Vault
        </NavLink>
      </nav>

      <main className="flex-1 min-w-0 pb-16 md:pb-0">{children}</main>
    </div>
  );
}

function requestLabel(r) {
  if (r.request_type === "continuation") return `Continuation · ${r.project_name || ""}`;
  if (r.request_type === "assistance")   return `Request · ${r.project_name || ""}`;
  return "New project";
}
function requestStatusTone(status) {
  switch (status) {
    case "converted": return "green";
    case "declined":  return "red";
    case "assigned":  return "blue";
    default:          return "orange";
  }
}

// Feb 2026 — Accountability Badges: single source of truth for both the
// ClientHome "Invoices" summary and the My Vault > Invoices tiles, so the
// two surfaces never drift on wording/colour.
function _daysBetween(fromISO, toISO) {
  return Math.round((new Date(toISO) - new Date(fromISO)) / 86400000);
}
function invoiceAccountabilityBadge(inv) {
  const paid = inv.status === "received" || inv.status === "paid";
  if (paid) {
    if (!inv.payment_received_date || !inv.due_date) return { label: "Paid", tone: "green" };
    if (inv.payment_received_date <= inv.due_date) return { label: "Paid on Time", tone: "green" };
    const daysLate = _daysBetween(inv.due_date, inv.payment_received_date);
    return { label: `Paid ${daysLate} day${daysLate === 1 ? "" : "s"} late`, tone: "red" };
  }
  if (!inv.due_date) return null;
  const diff = _daysBetween(new Date().toISOString().slice(0, 10), inv.due_date);
  if (diff < 0) return { label: `Delayed: ${Math.abs(diff)} Day${Math.abs(diff) === 1 ? "" : "s"}`, tone: "red" };
  return { label: `${diff} Day${diff === 1 ? "" : "s"} Remaining`, tone: "amber" };
}
function AccountabilityBadge({ inv, testId }) {
  const b = invoiceAccountabilityBadge(inv);
  if (!b) return null;
  const toneCls = b.tone === "green" ? "bg-green-100 text-green-700" : b.tone === "red" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
  return <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-medium whitespace-nowrap ${toneCls}`} data-testid={testId}>{b.label}</span>;
}

function ClientHeroBar() {
  const [hero, setHero] = useState(null);
  const [favBusy, setFavBusy] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [saved, setSaved] = useState([]);
  const load = () => api.get("/client/hero").then((r) => setHero(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!hero) return null;
  const { quote, quote_id, is_favorited, pulse, widget_visibility: vis } = hero;
  const hasPulse = (vis.holidays && pulse.holidays.length > 0)
    || (vis.birthdays && pulse.birthdays.length > 0)
    || (vis.anniversaries && pulse.anniversaries.length > 0)
    || (vis.shoutouts && pulse.shoutout);

  const toggleFavorite = async () => {
    if (!quote_id || favBusy) return;
    setFavBusy(true);
    try {
      if (is_favorited) await api.delete(`/client/quotes/${quote_id}/favorite`);
      else await api.post(`/client/quotes/${quote_id}/favorite`);
      await load();
    } catch { /* silent — non-critical UI affordance */ } finally { setFavBusy(false); }
  };

  const openSaved = async () => {
    setShowSaved(true);
    try { const r = await api.get("/client/quotes/favorites"); setSaved(r.data); } catch { setSaved([]); }
  };

  return (
    <div className="rounded-sm overflow-hidden mb-8 bg-[#0F172A] text-white relative" data-testid="client-hero-bar">
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#F54800]/20 rounded-full blur-3xl pointer-events-none" />
      <div className="relative grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
        <div className="p-6 sm:p-8 flex flex-col justify-center" data-testid="client-hero-quote">
          <div className="flex items-start justify-between gap-3">
            <Quote size={22} className="text-[#F54800] mb-3" />
            {quote_id && (
              <button
                onClick={toggleFavorite}
                disabled={favBusy}
                aria-pressed={is_favorited}
                className="text-white/40 hover:text-[#F54800] transition-colors disabled:opacity-40"
                title={is_favorited ? "Remove from saved quotes" : "Save this quote"}
                data-testid="client-hero-quote-favorite"
              >
                <Star size={17} className={is_favorited ? "fill-[#F54800] text-[#F54800]" : ""} />
              </button>
            )}
          </div>
          <p className="font-display text-xl sm:text-2xl leading-snug text-white/95">{quote}</p>
          <div className="flex items-center justify-between mt-4">
            <div className="text-[11px] text-white/40 uppercase tracking-wider">Studio principle · today</div>
            <button onClick={openSaved} className="text-[11px] text-white/50 hover:text-white/80 underline underline-offset-2" data-testid="client-hero-view-saved">
              My saved quotes
            </button>
          </div>
        </div>
        <div className="p-6 sm:p-8" data-testid="client-hero-pulse">
          <div className="text-[11px] text-white/40 uppercase tracking-wider mb-3">Studio pulse</div>
          {!hasPulse ? (
            <div className="text-sm text-white/50 italic">Nothing to celebrate today — check back tomorrow.</div>
          ) : (
            <div className="space-y-3">
              {vis.shoutouts && pulse.shoutout && (
                <div className="flex items-start gap-2.5 bg-[#F54800]/15 border border-[#F54800]/30 rounded-sm px-3 py-2" data-testid="client-hero-shoutout">
                  <Megaphone size={15} className="text-[#F54800] shrink-0 mt-0.5" />
                  <span className="text-sm text-white/90">{pulse.shoutout}</span>
                </div>
              )}
              {vis.holidays && pulse.holidays.map((h, i) => (
                <div key={`h-${i}`} className="flex items-center gap-2.5 text-sm text-white/85" data-testid={`client-hero-holiday-${i}`}>
                  <CalendarHeart size={15} className="text-[#F54800] shrink-0" /> Studio holiday today — {h}
                </div>
              ))}
              {vis.birthdays && pulse.birthdays.map((n, i) => (
                <div key={`b-${i}`} className="flex items-center gap-2.5 text-sm text-white/85" data-testid={`client-hero-birthday-${i}`}>
                  <Cake size={15} className="text-[#F54800] shrink-0" /> It's {n}'s birthday today!
                </div>
              ))}
              {vis.anniversaries && pulse.anniversaries.map((a, i) => (
                <div key={`a-${i}`} className="flex items-center gap-2.5 text-sm text-white/85" data-testid={`client-hero-anniversary-${i}`}>
                  <PartyPopper size={15} className="text-[#F54800] shrink-0" /> {a.name} celebrates {a.years} year{a.years === 1 ? "" : "s"} with Analogy today
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showSaved && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowSaved(false)} data-testid="client-saved-quotes-modal">
          <div className="bg-white rounded-sm max-w-md w-full max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <span className="font-display font-bold text-slate-900">My saved quotes</span>
              <button onClick={() => setShowSaved(false)} className="text-slate-400 hover:text-slate-700" data-testid="client-saved-quotes-close"><X size={16} /></button>
            </div>
            <div className="divide-y divide-slate-100">
              {saved.length === 0 ? (
                <div className="px-5 py-6 text-sm text-slate-500 text-center">No saved quotes yet — hit the star on today's quote to keep it.</div>
              ) : saved.map((q) => (
                <div key={q.id} className="px-5 py-3 text-sm text-slate-800" data-testid={`client-saved-quote-${q.id}`}>{q.text}</div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientHome() {
  const [projects, setProjects] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [chatProject, setChatProject] = useState(null);  // project for side panel
  const [myRequests, setMyRequests] = useState([]);
  const loadRequests = () => api.get("/client/requests").then((r) => setMyRequests(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/client/projects").then((r) => setProjects(r.data));
    loadRequests();
  }, []);
  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto fade-in" data-testid="client-home">
      <ClientNudgeBar />
      <ClientHeroBar />
      <GoogleReviewBanner />
      <div className="mb-8">
        <div className="label-overline text-[#F54800]">Studio portal</div>
        <h1 className="font-display text-4xl font-bold text-slate-900 tracking-tight">Your projects</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Real-time visibility into every project Analogy is building for you — milestones, reviews and progress at a glance.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {projects.map((p) => (
          <div key={p.id} className="card-flat overflow-hidden hover:shadow-sm group relative" data-testid={`client-project-${p.id}`}>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setChatProject(p); }}
              className="absolute top-3 right-3 z-10 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-sm p-2 hover:bg-[#F54800] hover:text-white hover:border-[#F54800] transition-colors shadow-sm"
              data-testid={`project-chat-icon-${p.id}`}
              title="Open chat with project team"
            >
              <MessageSquare size={14} />
            </button>
            <Link to={`/client/projects/${p.id}`} className="block">
              <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                {p.cover_image && <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />}
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2"><Tag tone="slate">{p.business_unit}</Tag><Tag tone={projectStatusTone(p.status)}>{p.status.replace("_", " ")}</Tag></div>
                <div className="font-display text-xl font-bold text-slate-900">{p.name}</div>
                {p.description && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{p.description}</p>}
                <div className="text-xs text-slate-500 mt-3 inline-flex items-center gap-1">View milestones <ChevronRight size={12} /></div>
              </div>
            </Link>
          </div>
        ))}
        {projects.length === 0 && <div className="text-sm text-slate-500">No projects assigned yet.</div>}
      </div>

      <div className="card-flat mt-8" data-testid="client-my-requests">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="font-display font-bold flex items-center gap-2">
            <Sparkles size={14} className="text-[#F54800]" /> Your requests with Analogy
          </div>
          <Button
            size="sm"
            onClick={() => setRequestOpen(true)}
            disabled={projects.length === 0}
            className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white"
            data-testid="add-request-btn"
          >
            <Plus size={13} className="mr-1" /> New request
          </Button>
        </div>
        {myRequests.length === 0 ? (
          <div className="px-5 py-6 text-xs text-slate-500 italic">No requests yet. Hit <b className="text-slate-700">+ New request</b> to ask the team for anything specific on one of your projects.</div>
        ) : (
          <ClientRequestGroups requests={myRequests} projects={projects} onReplied={loadRequests} />
        )}
      </div>

      <AssistanceRequestDialog open={requestOpen} onClose={() => setRequestOpen(false)} onSubmitted={loadRequests} projects={projects} />
      <ProjectChatPanel project={chatProject} onClose={() => setChatProject(null)} />

      <ClientInvoicesSummary />

      {/* Rate your team — relocated here (portfolio-wide) + Share a Testimonial */}
      <ClientTeamAppreciation />
      <ShareTestimonialCard />
    </div>
  );
}

// Feb 2026 — Client Portal Visibility: every Sent/Received/Overdue invoice
// for the client's own projects gets a home-page summary (latest 5), each
// carrying the same accountability badge + True Received Date timeline as
// the My Vault > Invoices tab, so clients never have to dig for status.
function ClientInvoicesSummary() {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/client/invoices").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoaded(true));
  }, []);
  const fmtAmount = (r) => `${r.currency === "INR" ? "₹" : r.currency + " "}${Number(r.amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  if (!loaded || rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 5);
  const outstandingCount = rows.filter((r) => r.status !== "received" && r.status !== "paid").length;
  return (
    <div className="card-flat mt-8" data-testid="client-invoices-summary">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
        <div className="font-display font-bold flex items-center gap-2">
          <Receipt size={14} className="text-[#F54800]" /> Invoices
          {outstandingCount > 0 && (
            <span className="text-[10px] font-medium bg-orange-100 text-[#F54800] px-1.5 py-0.5 rounded-sm" data-testid="client-invoices-outstanding-count">
              {outstandingCount} outstanding
            </span>
          )}
        </div>
        <Link to="/client/invoices" className="text-xs text-[#F54800] hover:underline inline-flex items-center gap-1" data-testid="view-all-invoices-link">
          View all <ChevronRight size={12} />
        </Link>
      </div>
      <div className="divide-y divide-slate-100">
        {sorted.map((r) => (
          <div key={r.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`client-home-invoice-${r.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{r.invoice_number || "Invoice"}</div>
              <div className="text-xs text-slate-500 truncate">{r.project_name} · {fmtAmount(r)}</div>
              {r.payment_received_date && (
                <div className="text-[11px] text-green-700 mt-0.5" data-testid={`client-home-invoice-received-${r.id}`}>Received · {formatDate(r.payment_received_date)}</div>
              )}
            </div>
            <AccountabilityBadge inv={r} testId={`client-home-invoice-badge-${r.id}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function AssistanceRequestDialog({ open, onClose, onSubmitted, projects }) {
  const [form, setForm] = useState({ project_id: "", title: "", brief: "", start_date: "", end_date: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && projects.length === 1) setForm((f) => ({ ...f, project_id: projects[0].id }));
  }, [open, projects]);

  const wd = useMemo(() => workingDaysBetween(form.start_date, form.end_date), [form.start_date, form.end_date]);
  const startDateObj = form.start_date ? new Date(form.start_date) : undefined;

  const submit = async () => {
    if (!form.project_id) { toast.error("Select a project"); return; }
    if (!form.title.trim() || !form.brief.trim()) { toast.error("Add a title and description"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Pick start and end dates"); return; }
    if (form.end_date < form.start_date) { toast.error("End date must be on/after start"); return; }
    setSaving(true);
    try {
      await api.post("/client/requests", {
        request_type: "assistance",
        project_id: form.project_id,
        title: form.title.trim(),
        brief: form.brief.trim(),
        start_date: form.start_date,
        end_date: form.end_date,
        duration_working_days: wd,
      });
      toast.success("Request sent to the team");
      setForm({ project_id: "", title: "", brief: "", start_date: "", end_date: "" });
      onClose();
      onSubmitted?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="assistance-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">New request with Analogy</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">Ask for something specific on an existing project — a tweak, a re-render, a quick exploration. Dates use working days (Mon–Fri).</p>
        <div className="space-y-3 mt-3">
          <div>
            <Label className="label-overline">Project</Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="assist-project-select"><SelectValue placeholder="Pick a project" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.business_unit}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-overline">Title</Label>
            <Input className="rounded-sm mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Add a third colourway to the renders" data-testid="assist-title" />
          </div>
          <div>
            <Label className="label-overline">Description</Label>
            <Textarea rows={4} className="rounded-sm mt-1" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} placeholder="What exactly are you asking for? Any references, links, or constraints?" data-testid="assist-brief" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-overline">Start / requested date</Label>
              <div className="mt-1"><WorkingDayPicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v, end_date: form.end_date && form.end_date < v ? "" : form.end_date })} testId="assist-start-date" /></div>
            </div>
            <div>
              <Label className="label-overline">End date</Label>
              <div className="mt-1"><WorkingDayPicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} minDate={startDateObj} disabled={!form.start_date} testId="assist-end-date" /></div>
            </div>
          </div>
          {form.start_date && form.end_date && (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-sm px-3 py-2" data-testid="assist-duration">
              Duration · <b className="text-slate-900">{wd} working day{wd === 1 ? "" : "s"}</b> (weekends excluded)
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="assist-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="assist-submit">{saving ? "Sending…" : "Send request"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectChatPanel({ project, onClose }) {
  return (
    <Sheet open={!!project} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="project-chat-panel">
        <SheetHeader className="px-5 py-3 border-b border-slate-200">
          <SheetTitle className="font-display text-base text-left flex items-center gap-2">
            <MessageSquare size={14} className="text-[#F54800]" /> {project?.name}
          </SheetTitle>
        </SheetHeader>
        {project && (
          <div className="flex-1 min-h-0 flex flex-col">
            <EmbeddedProjectChat projectId={project.id} projectName={project.name} embedded compact />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// Phase 81 — NewProjectRequestDialog removed. Clients can only create requests
// scoped to an existing project (via AssistanceRequestDialog / ContinueWorkDialog).


// ────────────────────────────────────────────────────────────────────────────
// Phase 81 — ClientRequestGroups
// Groups the client's own requests by project into collapsible accordions.
// Each thread supports GET/POST /api/client-requests/{rid}/replies. Replies
// hit the client / internal user immediately — NO approval flow.
// ────────────────────────────────────────────────────────────────────────────
function ClientRequestGroups({ requests, projects, onReplied }) {
  const [openProjects, setOpenProjects] = useState(() => {
    // Auto-open the first project group so the client sees content by default.
    const first = requests.find((r) => r.project_id)?.project_id;
    return first ? new Set([first]) : new Set();
  });
  const [openThread, setOpenThread] = useState(null);

  const groups = useMemo(() => {
    const byProject = new Map();
    for (const r of requests) {
      const key = r.project_id || "__orphan__";
      if (!byProject.has(key)) byProject.set(key, []);
      byProject.get(key).push(r);
    }
    const projectLookup = new Map(projects.map((p) => [p.id, p]));
    const out = [];
    for (const [pid, rows] of byProject.entries()) {
      const proj = projectLookup.get(pid);
      out.push({
        key: pid,
        project_id: pid === "__orphan__" ? null : pid,
        project_name: proj?.name || rows[0]?.project_name || (pid === "__orphan__" ? "New project enquiries" : "Untitled project"),
        business_unit: proj?.business_unit || rows[0]?.business_unit || "",
        rows: rows.sort((a, b) => (b.last_reply_at || b.created_at || "").localeCompare(a.last_reply_at || a.created_at || "")),
      });
    }
    // Sort by most recent activity
    out.sort((a, b) => {
      const aT = a.rows[0]?.last_reply_at || a.rows[0]?.created_at || "";
      const bT = b.rows[0]?.last_reply_at || b.rows[0]?.created_at || "";
      return bT.localeCompare(aT);
    });
    return out;
  }, [requests, projects]);

  const toggleProject = (pid) => {
    setOpenProjects((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  return (
    <div data-testid="client-request-groups">
      {groups.map((g) => {
        const isOpen = openProjects.has(g.key);
        return (
          <div key={g.key} className="border-b border-slate-100 last:border-0" data-testid={`crg-group-${g.key}`}>
            <button
              type="button"
              onClick={() => toggleProject(g.key)}
              className="w-full px-5 py-3 flex items-center gap-2 hover:bg-slate-50/60 text-left"
              data-testid={`crg-group-header-${g.key}`}
            >
              {isOpen ? <ChevronDown size={13} className="text-slate-400" /> : <ChevronRight size={13} className="text-slate-400" />}
              <FolderOpen size={13} className="text-[#F54800]" />
              <span className="font-medium text-sm text-slate-900 truncate">{g.project_name}</span>
              {g.business_unit && <span className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm">{g.business_unit}</span>}
              <span className="ml-auto text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm" data-testid={`crg-group-count-${g.key}`}>
                {g.rows.length} thread{g.rows.length === 1 ? "" : "s"}
              </span>
            </button>
            {isOpen && (
              <ul className="bg-slate-50/30">
                {g.rows.map((r) => (
                  <ClientRequestThread
                    key={r.id}
                    row={r}
                    open={openThread === r.id}
                    onToggle={() => setOpenThread(openThread === r.id ? null : r.id)}
                    onReplied={onReplied}
                  />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ClientRequestThread({ row: r, open, onToggle, onReplied }) {
  const [replies, setReplies] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/client-requests/${r.id}/replies`)
      .then(({ data }) => setReplies(data || []))
      .catch(() => setReplies([]));
  }, [open, r.id]);

  const submit = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/client-requests/${r.id}/replies`, { body: draft.trim() });
      setReplies((arr) => [...(arr || []), data]);
      setDraft("");
      toast.success("Reply sent to the Analogy team");
      onReplied?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const when = (r.last_reply_at || r.created_at || "").slice(0, 16).replace("T", " ");

  return (
    <li className="border-t border-slate-100 first:border-0" data-testid={`crg-thread-${r.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full pl-10 pr-5 py-2.5 flex items-start gap-2 hover:bg-white text-left"
        data-testid={`crg-thread-header-${r.id}`}
      >
        {open ? <ChevronDown size={12} className="text-slate-400 mt-1" /> : <ChevronRight size={12} className="text-slate-400 mt-1" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-slate-900 truncate">{r.title || requestLabel(r)}</span>
            {r.status && (
              <Tag tone={requestStatusTone(r.status)}>{r.status}</Tag>
            )}
            {r.reply_count > 0 && (
              <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-sm">
                {r.reply_count} repl{r.reply_count === 1 ? "y" : "ies"}
              </span>
            )}
            {r.last_reply_side === "internal" && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm">Team replied</span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {r.request_type} · {when}
          </div>
        </div>
      </button>
      {open && (
        <div className="pl-10 pr-5 pb-4 space-y-2.5 bg-white" data-testid={`crg-thread-body-${r.id}`}>
          <div className="p-3 bg-slate-50 rounded-sm border border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Your original brief</div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">{r.brief}</div>
          </div>
          {replies === null && <div className="text-xs text-slate-400 italic">Loading replies…</div>}
          {(replies || []).map((rep) => (
            <div
              key={rep.id}
              className={`p-3 rounded-sm border ${rep.author_side === "client" ? "border-blue-100 bg-blue-50/40" : "border-slate-200 bg-white"}`}
              data-testid={`crg-reply-${rep.id}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                {rep.author_name} · {rep.author_side === "client" ? "You" : "Analogy team"} · {(rep.created_at || "").slice(0, 16).replace("T", " ")}
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{rep.body}</div>
            </div>
          ))}
          <div className="pt-1">
            <Textarea
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Reply to the Analogy team…"
              className="rounded-sm text-sm"
              data-testid={`crg-reply-input-${r.id}`}
            />
            <div className="flex items-center justify-end mt-1.5">
              <Button
                size="sm"
                onClick={submit}
                disabled={busy || !draft.trim()}
                className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white"
                data-testid={`crg-reply-send-${r.id}`}
              >
                <Send size={11} className="mr-1" /> {busy ? "Sending…" : "Send reply"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}




function ClientProject() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectForm, setRejectForm] = useState({ reason: "", q1: "", q2: "", q3: "" });
  const [testForm, setTestForm] = useState({ rating: 5, testimonial: "", google_review_consent: false });
  const [testSent, setTestSent] = useState(false);
  const [continueOpen, setContinueOpen] = useState(false);

  const load = useCallback(() => api.get(`/client/projects/${id}/overview`).then((r) => setData(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  const decide = async (m, decision) => {
    if (decision === "rejected") { setRejectFor(m); return; }
    try {
      await api.patch(`/milestones/${m.id}`, { client_decision: "approved", client_decided_at: new Date().toISOString() });
      toast.success("Approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReject = async () => {
    const len = rejectForm.reason.trim().length;
    if (len < 500) { toast.error(`Need at least 500 characters of feedback (you have ${len}).`); return; }
    try {
      await api.patch(`/milestones/${rejectFor.id}`, {
        client_decision: "rejected",
        reject_reason: rejectForm.reason,
        scope_answers: { q1: rejectForm.q1, q2: rejectForm.q2, q3: rejectForm.q3 },
      });
      toast.success("Feedback sent to the team");
      setRejectFor(null); setRejectForm({ reason: "", q1: "", q2: "", q3: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rateMilestone = async (m, stars) => {
    try { await api.patch(`/milestones/${m.id}`, { client_rating: stars }); load(); toast.success(stars === 0 ? "Rating cleared" : `Rated ${stars}★`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const sendTestimonial = async () => {
    try { await api.post("/client/testimonials", { project_id: id, ...testForm }); setTestSent(true); toast.success("Thank you!"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const approveReview = async (r) => {
    try {
      await api.post(`/client/reviews/${r.id}/approve`);
      toast.success("Approved — the team has been notified");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;
  const { project, milestones, reviews, progress_pct, days_to_next_milestone, upcoming_milestones, priorities, finance_summary } = data;
  const done = milestones.filter((m) => m.status === "done").length;
  const allDecided = milestones.length > 0 && milestones.every((m) => m.client_decision);
  const allCompleted = milestones.length > 0 && done === milestones.length && allDecided;
  const pendingReviews = (reviews || []).filter((r) => r.status === "pending");
  const goals = project.goals || [];
  const problems = project.problems || [];
  const outcomes = project.outcomes || [];
  const hasScope = goals.length || problems.length || outcomes.length;

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto fade-in" data-testid="client-project">
      <Link to="/client" className="text-xs text-slate-500 hover:text-slate-900">← Back to projects</Link>
      <div className="card-flat overflow-hidden my-6" data-testid="client-project-hero">
        {project.cover_image && <div className="aspect-[3/1] bg-slate-100"><img src={project.cover_image} alt="" className="w-full h-full object-cover" /></div>}
        <div className="p-6">
          <div className="flex items-center gap-2 mb-2"><Tag tone="slate">{project.business_unit}</Tag><Tag tone={projectStatusTone(project.status)}>{project.status.replace("_", " ")}</Tag></div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{project.name}</h1>
          {project.description && <p className="text-slate-700 mt-3 max-w-3xl">{project.description}</p>}
          <div className="mt-5 flex flex-wrap items-end gap-6">
            <div className="flex-1 min-w-[200px]">
              <div className="label-overline mb-1 flex items-center justify-between">
                <span>Progress · {done}/{milestones.length} milestones</span>
                <span className="text-[#F54800] font-bold" data-testid="client-progress-pct">{progress_pct}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                <div className="h-full bg-[#F54800] transition-all duration-500" style={{ width: `${progress_pct}%` }} />
              </div>
            </div>
            {days_to_next_milestone !== null && (
              <div className="shrink-0" data-testid="client-days-to-milestone">
                <div className="label-overline mb-1">Next milestone</div>
                <div className={`font-display text-2xl font-bold ${days_to_next_milestone < 0 ? "text-red-600" : "text-slate-900"}`}>
                  {days_to_next_milestone < 0 ? `${Math.abs(days_to_next_milestone)}d overdue` : days_to_next_milestone === 0 ? "Today" : `${days_to_next_milestone} day${days_to_next_milestone === 1 ? "" : "s"}`}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Strategic Pulse — Active Priorities + Upcoming Milestones, side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6" data-testid="client-strategic-pulse">
        <div className="card-flat p-5" data-testid="client-priorities-card">
          <div className="font-display font-bold flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-[#F54800]" /> Active priorities this sprint
          </div>
          {priorities.length === 0 ? (
            <div className="text-xs text-slate-400 italic">No open priorities right now.</div>
          ) : (
            <ul className="space-y-2.5">
              {priorities.map((p, i) => (
                <li key={p.id} className="flex items-start gap-2.5 text-sm" data-testid={`client-priority-${p.id}`}>
                  <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-[#F54800]/10 text-[#F54800] text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
                  <div className="min-w-0">
                    <div className="text-slate-900 break-words">{p.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      {p.milestone_title && <span>{p.milestone_title}</span>}
                      {p.due_date && <span>Due {formatDate(p.due_date)}</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="card-flat p-5" data-testid="client-upcoming-milestones-card">
          <div className="font-display font-bold flex items-center gap-2 mb-3">
            <Target size={14} className="text-[#F54800]" /> Upcoming milestones
          </div>
          {upcoming_milestones.length === 0 ? (
            <div className="text-xs text-slate-400 italic">Nothing scheduled — all milestones are done or unscheduled.</div>
          ) : (
            <ul className="space-y-2.5">
              {upcoming_milestones.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`client-upcoming-ms-${m.id}`}>
                  <span className="text-slate-900 min-w-0 break-words">{m.title}</span>
                  <span className="shrink-0 flex items-center gap-2">
                    <span className="text-[11px] text-slate-500">{formatDate(m.due_date)}</span>
                    <Tag tone={m.on_time ? "green" : "red"}>{m.on_time ? "On-time" : "At risk"}</Tag>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Goals TL;DR */}
      {hasScope ? (
        <div className="card-flat mb-6" data-testid="project-scope-tldr">
          <div className="px-5 py-3 border-b border-slate-200 font-display font-bold flex items-center gap-2">
            <Target size={14} className="text-[#F54800]" /> Project goals
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
            <ScopeColumn label="Goals" items={goals} tone="green" testId="scope-goals" />
            <ScopeColumn label="Problems to solve" items={problems} tone="orange" testId="scope-problems" />
            <ScopeColumn label="Expected outcomes" items={outcomes} tone="brand" testId="scope-outcomes" />
          </div>
          {milestones.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/40">
              <div className="label-overline mb-1">Milestones delivering those outcomes</div>
              <div className="flex flex-wrap gap-1.5">
                {milestones.map((m, i) => (
                  <Tag key={m.id} tone={m.status === "done" ? "green" : "slate"} data-testid={`scope-ms-pill-${i}`}>
                    {String(i + 1).padStart(2, "0")} · {m.title}
                  </Tag>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card-flat mb-6 p-4 text-xs text-slate-500 flex items-start gap-2" data-testid="project-scope-empty">
          <AlertCircle size={14} className="text-slate-400 mt-0.5 shrink-0" />
          Your project lead hasn&apos;t filled in the goals / problems / outcomes for this project yet. Once they do, the TL;DR will appear here.
        </div>
      )}

      {/* Finance summary — client's own billing only, no P&L/internal costs */}
      <div className="card-flat mb-6" data-testid="client-finance-summary">
        <div className="px-5 py-3 border-b border-slate-200 font-display font-bold flex items-center gap-2">
          <Receipt size={14} className="text-[#F54800]" /> Finance summary
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
          <div className="p-5" data-testid="client-finance-total-billed">
            <div className="label-overline mb-1">Total billed</div>
            <div className="font-display text-2xl font-bold text-slate-900">₹ {Number(finance_summary.total_billed || 0).toLocaleString("en-IN")}</div>
          </div>
          <div className="p-5" data-testid="client-finance-amount-paid">
            <div className="label-overline mb-1">Amount paid</div>
            <div className="font-display text-2xl font-bold text-green-700">₹ {Number(finance_summary.amount_paid || 0).toLocaleString("en-IN")}</div>
          </div>
          <div className="p-5" data-testid="client-finance-outstanding">
            <div className="label-overline mb-1">Outstanding balance</div>
            <div className={`font-display text-2xl font-bold ${finance_summary.outstanding_balance > 0 ? "text-[#F54800]" : "text-slate-900"}`}>₹ {Number(finance_summary.outstanding_balance || 0).toLocaleString("en-IN")}</div>
          </div>
        </div>
      </div>

      {/* Project updates feed — replaces the embedded chat */}
      <ProjectUpdatesFeed projectId={id} />

      {/* Phase 22 — Prototype iteration visual feed (only renders on physical_product projects) */}
      <ClientIterationFeed projectId={id} />

      <div className="card-flat mt-6">
        <div className="px-5 py-3 border-b border-slate-200 font-display font-bold">Milestones · approve or share concerns</div>
        <div className="divide-row">
          {milestones.map((m, i) => (
            <div key={m.id} className="px-5 py-4 hover-row" data-testid={`client-ms-${m.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="font-mono text-xs text-slate-400 w-6 mt-0.5">{String(i + 1).padStart(2, "0")}</div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">{m.title}</div>
                    <div className="text-xs text-slate-500">Due {formatDate(m.due_date)}</div>
                    {m.client_decision === "rejected" && m.reject_reason && (
                      <div className="text-xs text-red-700 mt-1 italic">Your note: &quot;{m.reject_reason}&quot;</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                  <Tag tone={milestoneStatusTone(m.status)}>{(m.status || "").replace("_", " ")}</Tag>
                  {m.client_decision === "approved" && <Tag tone="green">approved</Tag>}
                  {m.client_decision === "rejected" && <Tag tone="red">rejected</Tag>}
                  {!m.client_decision && m.status === "done" && (
                    <>
                      <Button size="sm" className="rounded-sm h-7 bg-green-600 hover:bg-green-700 text-white" onClick={() => decide(m, "approved")} data-testid={`approve-ms-${m.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => decide(m, "rejected")} data-testid={`reject-ms-${m.id}`}>Reject</Button>
                    </>
                  )}
                </div>
              </div>
              {m.client_decision === "approved" && (
                <div className="ml-10 mt-2 flex items-center gap-1 text-xs text-slate-500 flex-wrap">
                  <span className="mr-1">Rate this milestone (0–5):</span>
                  <button onClick={() => rateMilestone(m, 0)} className={`text-xs px-1.5 py-0.5 border rounded-sm ${(m.client_rating ?? -1) === 0 ? "border-[#F54800] text-[#F54800]" : "border-slate-200 text-slate-400 hover:text-slate-600"}`} data-testid={`rate-ms-${m.id}-0`} title="Clear / 0">0</button>
                  {[1,2,3,4,5].map((s) => (
                    <button key={s} onClick={() => rateMilestone(m, s)} className={`text-lg leading-none ${(m.client_rating || 0) >= s ? "text-[#F54800]" : "text-slate-300 hover:text-slate-400"}`} data-testid={`rate-ms-${m.id}-${s}`}>★</button>
                  ))}
                  {(m.client_rating ?? null) !== null && <span className="ml-2 text-slate-500">· {m.client_rating}/5</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Share why this needs more work</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Before rejecting, please help us understand. A few quick questions ensure we&apos;re aligned and not drifting out of scope.</p>
          <div className="space-y-3 mt-3">
            <div>
              <Label className="label-overline">What specifically is missing? <span className="text-red-600">(min 500 chars)</span></Label>
              <Textarea rows={6} className="rounded-sm mt-1" value={rejectForm.reason} onChange={(e) => setRejectForm({ ...rejectForm, reason: e.target.value })} data-testid="reject-reason" />
              <div className="mt-1 flex items-center justify-between text-[11px]">
                <span className="text-slate-500">Give the team at least 500 characters of clear, specific feedback so they know exactly what to fix.</span>
                <span className={rejectForm.reason.trim().length >= 500 ? "text-green-600 font-medium" : "text-red-600 font-medium"} data-testid="reject-char-count">
                  {rejectForm.reason.trim().length} / 500
                </span>
              </div>
            </div>
            <div><Label className="label-overline">Is this within the original scope we agreed?</Label><Input className="rounded-sm mt-1" placeholder="Yes / No / Not sure — explain" value={rejectForm.q1} onChange={(e) => setRejectForm({ ...rejectForm, q1: e.target.value })} data-testid="reject-q1" /></div>
            <div><Label className="label-overline">If out of scope, are you open to a change-order?</Label><Input className="rounded-sm mt-1" value={rejectForm.q2} onChange={(e) => setRejectForm({ ...rejectForm, q2: e.target.value })} data-testid="reject-q2" /></div>
            <div><Label className="label-overline">What outcome would make you approve this?</Label><Input className="rounded-sm mt-1" value={rejectForm.q3} onChange={(e) => setRejectForm({ ...rejectForm, q3: e.target.value })} data-testid="reject-q3" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={submitReject} disabled={rejectForm.reason.trim().length < 500} className="rounded-sm bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300 disabled:cursor-not-allowed" data-testid="reject-submit">Send rejection</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Continue work CTA — only when all milestones approved & done */}
      {allCompleted && (
        <div className="card-flat p-6 mt-6 border-l-4 border-l-[#F54800] flex flex-wrap items-center justify-between gap-3" data-testid="continue-work-card">
          <div className="min-w-0">
            <div className="label-overline text-[#F54800]">All milestones approved</div>
            <div className="font-display text-xl font-bold text-slate-900 mt-1">Ready for what&apos;s next?</div>
            <p className="text-sm text-slate-600 mt-1 max-w-xl">Have a Phase 2, a follow-up, or a new direction in mind? Drop a brief — it lands directly in your BU head&apos;s inbox.</p>
          </div>
          <Button onClick={() => setContinueOpen(true)} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="continue-work-btn">
            <ArrowRight size={14} className="mr-1.5" /> Continue work
          </Button>
        </div>
      )}

      <ContinueWorkDialog open={continueOpen} onClose={() => setContinueOpen(false)} project={project} />

      {allCompleted && !testSent && (
        <div className="card-flat p-6 mt-6 border-l-4 border-l-[#F54800]" data-testid="testimonial-card">
          <div className="label-overline text-[#F54800] mb-2">Project wrap</div>
          <div className="font-display text-xl font-bold text-slate-900">How was working with Analogy?</div>
          <p className="text-sm text-slate-600 mt-1">A 30-second testimonial helps us — and shows future founders what to expect.</p>
          <div className="mt-4">
            <div className="flex items-center gap-1 text-2xl">
              {[1,2,3,4,5].map((s) => (
                <button key={s} onClick={() => setTestForm({ ...testForm, rating: s })} className={(testForm.rating >= s) ? "text-[#F54800]" : "text-slate-300"} data-testid={`overall-rate-${s}`}>★</button>
              ))}
            </div>
            <Textarea rows={4} className="rounded-sm mt-3" placeholder="Share a sentence or two we can put on our website…" value={testForm.testimonial} onChange={(e) => setTestForm({ ...testForm, testimonial: e.target.value })} data-testid="testimonial-text" />
            <label className="flex items-center gap-2 mt-3 text-xs text-slate-600">
              <input type="checkbox" checked={testForm.google_review_consent} onChange={(e) => setTestForm({ ...testForm, google_review_consent: e.target.checked })} className="accent-[#F54800]" />
              I&apos;d be happy to also leave this as a Google review.
            </label>
            <Button onClick={sendTestimonial} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white mt-3" data-testid="testimonial-submit">Send testimonial</Button>
          </div>
        </div>
      )}

      {reviews?.length > 0 && (
        <div className="card-flat mt-6" data-testid="client-pending-reviews">
          <div className="px-5 py-3 border-b border-slate-200 font-display font-bold">Needs your review</div>
          <div className="divide-row">
            {pendingReviews.map((r) => (
              <div key={r.id} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`client-review-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{r.title}</div>
                  {r.description && <div className="text-xs text-slate-500 mt-0.5">{r.description}</div>}
                  {r.link && <a href={r.link} target="_blank" rel="noreferrer" className="text-xs text-[#F54800] hover:underline mt-1 inline-block">Open</a>}
                </div>
                <Button
                  size="sm"
                  onClick={() => approveReview(r)}
                  className="rounded-sm h-8 bg-green-600 hover:bg-green-700 text-white shrink-0"
                  data-testid={`client-approve-review-${r.id}`}
                >
                  Approve
                </Button>
              </div>
            ))}
            {pendingReviews.length === 0 && <div className="px-5 py-3 text-xs text-slate-500">Nothing awaiting your review.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function ClientTeamAppreciation() {
  const [team, setTeam] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const load = () => api.get("/client/team").then((r) => {
    setTeam(r.data);
    const d = {};
    (r.data || []).forEach((m) => { d[m.id] = { rating: m.my_rating || 0, feedback: m.my_feedback || "" }; });
    setDrafts(d);
  }).catch(() => setTeam([]));
  useEffect(() => { load(); }, []);

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const submit = async (m) => {
    const draft = drafts[m.id] || {};
    if (!draft.rating) { toast.error("Pick a star rating first"); return; }
    setSavingId(m.id);
    try {
      await api.post("/client/appreciations", {
        team_member_id: m.id, rating: draft.rating, feedback: draft.feedback || "",
      });
      toast.success(`Thanks for the feedback on ${m.name}!`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingId(null); }
  };

  if (!team || team.length === 0) return null;

  return (
    <div className="card-flat mt-6" data-testid="client-team-appreciation">
      <div className="px-5 py-3 border-b border-slate-200 font-display font-bold flex items-center gap-2">
        <Star size={14} className="text-[#F54800]" /> Rate your team
      </div>
      <div className="divide-y divide-slate-100">
        {team.map((m) => {
          const draft = drafts[m.id] || { rating: 0, feedback: "" };
          return (
            <div key={m.id} className="px-5 py-4" data-testid={`client-appreciation-${m.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-medium text-slate-900">{m.name}</div>
                  <div className="text-[11px] text-slate-500 capitalize">{(m.role || "").replace("_", " ")}</div>
                </div>
                <div className="flex items-center gap-1 text-xl" data-testid={`client-appreciation-${m.id}-stars`}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      onClick={() => setDraft(m.id, { rating: s })}
                      className={draft.rating >= s ? "text-[#F54800]" : "text-slate-300 hover:text-slate-400"}
                      data-testid={`client-appreciation-${m.id}-star-${s}`}
                    >★</button>
                  ))}
                </div>
              </div>
              <Textarea
                rows={2}
                className="rounded-sm mt-2 text-sm"
                placeholder={`Anything you'd like to share about working with ${m.name.split(" ")[0]}?`}
                value={draft.feedback}
                onChange={(e) => setDraft(m.id, { feedback: e.target.value })}
                data-testid={`client-appreciation-${m.id}-feedback`}
              />
              <Button
                size="sm"
                onClick={() => submit(m)}
                disabled={savingId === m.id}
                className="rounded-sm h-8 mt-2 bg-[#F54800] hover:bg-[#D63E00] text-white"
                data-testid={`client-appreciation-${m.id}-submit`}
              >
                {savingId === m.id ? "Saving…" : (m.my_rating ? "Update rating" : "Submit rating")}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Phase 139 — Always-available testimonial field, sitting right under "Rate your team". Distinct from the
 * project-wrap "How was working with Analogy?" card (which only appears once all milestones on a single
 * project are done) — this one is open any time, portfolio-wide, and lands in CRM + notifies Founders. */
function ShareTestimonialCard() {
  const [text, setText] = useState("");
  const [rating, setRating] = useState(0);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!text.trim()) { toast.error("Write a line or two first"); return; }
    if (!rating) { toast.error("Pick a star rating first"); return; }
    setSending(true);
    try {
      await api.post("/client/testimonials", { testimonial: text.trim(), rating });
      toast.success("Thank you! Your testimonial has been shared with our founders.");
      setText(""); setRating(0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="card-flat mt-6" data-testid="share-testimonial-card">
      <div className="px-5 py-3 border-b border-slate-200 font-display font-bold flex items-center gap-2">
        <Quote size={14} className="text-[#F54800]" /> Share a testimonial
      </div>
      <div className="px-5 py-4">
        <p className="text-xs text-slate-500 mb-3">A line or two about your experience with Analogy — this goes straight to our founders and helps future clients know what to expect.</p>
        <div className="flex items-center gap-1 text-xl mb-2" data-testid="share-testimonial-stars">
          {[1, 2, 3, 4, 5].map((s) => (
            <button key={s} onClick={() => setRating(s)} className={rating >= s ? "text-[#F54800]" : "text-slate-300 hover:text-slate-400"} data-testid={`share-testimonial-star-${s}`}>★</button>
          ))}
        </div>
        <Textarea
          rows={3}
          className="rounded-sm text-sm"
          placeholder="e.g. Analogy nailed our brand identity in a fraction of the time we expected…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="share-testimonial-text"
        />
        <Button size="sm" onClick={submit} disabled={sending} className="rounded-sm h-8 mt-2 bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="share-testimonial-submit">
          {sending ? "Sending…" : "Send testimonial"}
        </Button>
      </div>
    </div>
  );
}

/** Phase 139 — high-visibility "Leave a Google Review" CTA + QR modal, sitting right under the Hero.
 * Phase 144 — added a direct "Leave a Review on Desktop" button (opens the studio's
 * direct Google review link in a new tab) alongside the QR-scan option, for
 * desktop clients who'd rather click straight through than scan their own screen. */
function GoogleReviewBanner() {
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState(null);

  useEffect(() => { api.get("/branding/google-review-qr").then(({ data }) => setQr(data)).catch(() => {}); }, []);

  return (
    <>
      <div
        className="mb-8 rounded-sm bg-slate-900 text-white px-5 py-4 flex items-center justify-between gap-4 flex-wrap border-l-4 border-l-[#F54800]"
        data-testid="google-review-banner"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-[#F54800]/20 flex items-center justify-center shrink-0"><Star size={16} className="text-[#F54800]" /></div>
          <div className="min-w-0">
            <div className="font-display font-bold text-sm">Loved working with us?</div>
            <div className="text-xs text-slate-300">A quick Google review helps other founders find us.</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {qr?.review_link && (
            <Button
              onClick={() => window.open(qr.review_link, "_blank", "noopener,noreferrer")}
              className="rounded-sm bg-white hover:bg-slate-100 text-slate-900"
              data-testid="leave-review-desktop-btn"
            >
              <Monitor size={14} className="mr-1.5" /> Leave a Review on Desktop
            </Button>
          )}
          <Button onClick={() => setOpen(true)} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="leave-google-review-btn">
            <QrCode size={14} className="mr-1.5" /> Scan QR
          </Button>
        </div>
      </div>
      <GoogleReviewQrModal open={open} onClose={() => setOpen(false)} qr={qr} />
    </>
  );
}

function GoogleReviewQrModal({ open, onClose, qr }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-sm text-center" data-testid="google-review-qr-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Scan to leave a review</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          {qr?.url ? (
            <img src={qr.url} alt="Scan to leave a Google review" className="w-48 h-48 object-contain border border-slate-200 rounded-sm" data-testid="google-review-qr-image" />
          ) : (
            <div className="w-48 h-48 flex items-center justify-center border border-slate-200 rounded-sm text-xs text-slate-400 text-center px-4" data-testid="google-review-qr-placeholder">
              QR code coming soon — check back shortly!
            </div>
          )}
          <p className="text-xs text-slate-500">Point your phone&apos;s camera at the code above, or use the button below on this device.</p>
          {qr?.review_link && (
            <Button
              onClick={() => window.open(qr.review_link, "_blank", "noopener,noreferrer")}
              className="w-full rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white"
              data-testid="leave-review-desktop-btn-modal"
            >
              <Monitor size={14} className="mr-1.5" /> Leave a Review on Desktop
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Phase 139 — slim automated Nudge Bar: pending-approval alert + monthly Google review prompt. */
function ClientNudgeBar() {
  const [nudges, setNudges] = useState([]);
  const [qrOpen, setQrOpen] = useState(false);
  const [qr, setQr] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/client/nudges").then((r) => setNudges(r.data?.nudges || [])).catch(() => setNudges([]));
  }, []);

  const openReviewModal = () => {
    api.get("/branding/google-review-qr").then(({ data }) => setQr(data)).catch(() => {});
    setQrOpen(true);
  };

  const dismiss = async (n) => {
    setNudges((cur) => cur.filter((x) => x.type !== n.type));
    if (n.type === "google_review") {
      try { await api.post("/client/nudges/dismiss", { type: "google_review" }); } catch (e) { /* non-critical */ }
    }
  };

  if (nudges.length === 0) return null;

  return (
    <div className="mb-4 space-y-2" data-testid="client-nudge-bar">
      {nudges.map((n) => (
        <div
          key={n.type}
          className="rounded-sm bg-[#F54800] text-white px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap"
          data-testid={`client-nudge-${n.type}`}
        >
          <div className="flex items-center gap-2 text-sm min-w-0">
            <AlertTriangle size={14} className="shrink-0" />
            <span className="min-w-0">{n.message}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => (n.type === "google_review" ? openReviewModal() : nav(n.cta_href))}
              className="text-xs font-semibold underline hover:no-underline"
              data-testid={`client-nudge-${n.type}-cta`}
            >
              {n.cta_label}
            </button>
            <button onClick={() => dismiss(n)} className="text-white/80 hover:text-white" data-testid={`client-nudge-${n.type}-dismiss`}>
              <X size={14} />
            </button>
          </div>
        </div>
      ))}
      <GoogleReviewQrModal open={qrOpen} onClose={() => setQrOpen(false)} qr={qr} />
    </div>
  );
}


function ScopeColumn({ label, items, tone, testId }) {
  return (
    <div className="p-5" data-testid={testId}>
      <div className="label-overline mb-2 flex items-center gap-1.5"><Tag tone={tone}>{label}</Tag></div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 italic">Not specified yet.</div>
      ) : (
        <ul className="space-y-1.5 text-sm text-slate-700">
          {items.map((it, i) => (
            <li key={`${i}:${it}`} className="flex items-start gap-2"><span className="text-[#F54800] mt-1">•</span><span className="break-words">{it}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ContinueWorkDialog({ open, onClose, project }) {
  const [form, setForm] = useState({ title: "", brief: "", budget_hint: "", timeline_hint: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.title.trim() || !form.brief.trim()) { toast.error("Add a title and brief"); return; }
    setSaving(true);
    try {
      await api.post("/client/requests", {
        request_type: "continuation",
        project_id: project.id,
        business_unit: project.business_unit,
        ...form,
      });
      toast.success("Sent to your BU head — they'll reach out shortly");
      setForm({ title: "", brief: "", budget_hint: "", timeline_hint: "" });
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="continue-work-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Continue work on {project?.name}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">This lands in your BU head&apos;s inbox as a task. The faster the brief, the faster the kickoff.</p>
        <div className="space-y-3 mt-3">
          <div><Label className="label-overline">What&apos;s the next phase?</Label><Input className="rounded-sm mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Production-ready prototype" data-testid="continue-title" /></div>
          <div><Label className="label-overline">Brief — what & why</Label><Textarea rows={5} className="rounded-sm mt-1" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} placeholder="Outcome you want, what should change vs. the original scope, any constraints…" data-testid="continue-brief" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-overline">Budget (optional)</Label><Input className="rounded-sm mt-1" value={form.budget_hint} onChange={(e) => setForm({ ...form, budget_hint: e.target.value })} /></div>
            <div><Label className="label-overline">Timeline (optional)</Label><Input className="rounded-sm mt-1" value={form.timeline_hint} onChange={(e) => setForm({ ...form, timeline_hint: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="continue-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="continue-submit">{saving ? "Sending…" : "Send to BU head"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Shared chat helpers for the client portal — save-for-later bookmarks and
// lightweight "X is typing…" indicators over the existing message WebSocket.
// ---------------------------------------------------------------------------
function useChatSaved() {
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [savedList, setSavedList] = useState([]);
  const loadSaved = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/saved");
      setSavedList(data || []);
      setSavedIds(new Set((data || []).map((r) => r.message_id)));
    } catch (err) { logger.debug("load saved failed:", err?.message || err); }
  }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);
  const toggleSave = useCallback(async (mid) => {
    try {
      const { data } = await api.post(`/chat/messages/${mid}/save`, {});
      setSavedIds((prev) => { const n = new Set(prev); if (data.saved) n.add(mid); else n.delete(mid); return n; });
      loadSaved();
      toast.success(data.saved ? "Saved for later" : "Removed from Saved");
    } catch (err) { toast.error(formatApiError(err)); }
  }, [loadSaved]);
  return { savedIds, savedList, toggleSave };
}

function SaveMsgButton({ mid, saved, onToggle, mine }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(mid)}
      title={saved ? "Remove from Saved" : "Save for later"}
      className={`shrink-0 p-1 rounded transition-colors ${saved ? "text-[#F54800]" : mine ? "text-white/60 hover:text-white" : "text-slate-400 hover:text-[#F54800]"}`}
      data-testid={`client-save-btn-${mid}`}
    >
      {saved ? <BookmarkCheck size={13} /> : <Bookmark size={13} />}
    </button>
  );
}

function ClientSavedModal({ open, onClose, savedList, onJump, onRemove }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="client-saved-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Bookmark size={16} className="text-[#F54800]" /> Saved for later</DialogTitle></DialogHeader>
        <div className="mt-1 max-h-[55vh] overflow-y-auto divide-y divide-slate-100" data-testid="client-saved-list">
          {savedList.length === 0 && (
            <div className="py-8 text-center text-xs text-slate-400">No saved messages yet. Tap the bookmark on any message to keep it here.</div>
          )}
          {savedList.map((item) => (
            <div key={item.id} className="py-2 px-1 flex items-start gap-2" data-testid={`client-saved-item-${item.message_id}`}>
              <button onClick={() => onJump(item)} className="flex-1 min-w-0 text-left hover:bg-slate-50 rounded-sm px-1 py-0.5" data-testid={`client-saved-jump-${item.message_id}`}>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-0.5">
                  <Hash size={11} /><span className="font-medium truncate">{item.scope_label}</span>
                  <span className="shrink-0">· {item.sender_name}</span>
                </div>
                <div className="text-sm text-slate-700 line-clamp-2">{item.text || "📎 attachment"}</div>
              </button>
              <button onClick={() => onRemove(item.message_id)} title="Remove from Saved" className="p-1 rounded-sm text-[#F54800] hover:bg-[#F54800]/10 shrink-0" data-testid={`client-saved-remove-${item.message_id}`}>
                <BookmarkCheck size={15} />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EmbeddedProjectChat({ projectId, projectName, embedded, compact }) {
  const { user } = useAuth();
  const [channel, setChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const listRef = useRef(null);
  const channelRef = useRef(null);
  const wsRef = useRef(null);
  const { savedIds, savedList, toggleSave } = useChatSaved();
  const [savedOpen, setSavedOpen] = useState(false);
  const { typingUsers, setChannel: setTypingChannel, handleWsMsg, clearSender, sendTyping } = useChatTyping(wsRef, user?.id);
  useEffect(() => { channelRef.current = channel; setTypingChannel(channel?.id || null); }, [channel, setTypingChannel]);
  const jumpToSaved = (item) => {
    setSavedOpen(false);
    setTimeout(() => document.getElementById(`cmsg-${item.message_id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  useEffect(() => {
    api.get("/client/chat/channels")
      .then(({ data }) => {
        const match = (data || []).find((c) => c.project_id === projectId);
        if (match) setChannel(match);
      })
      .catch(() => {});
  }, [projectId]);

  useEffect(() => {
    if (!channel) return;
    api.get("/chat/messages", { params: { channel_id: channel.id } })
      .then(({ data }) => {
        setMessages(data);
        setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
      })
      .catch(() => {});
  }, [channel]);

  useEffect(() => {
    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === "typing") { handleWsMsg(msg); return; }
      const cur = channelRef.current;
      if (cur && msg.channel_id === cur.id) {
        clearSender(msg.sender_id);
        setMessages((m) => {
          if (m.some((x) => x.id === msg.id)) return m.map((x) => (x.id === msg.id ? { ...x, ...msg } : x));
          setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
          return [...m, msg];
        });
      }
    };
    ws.onerror = (err) => logger.warn("Client WS error:", err);
    return () => { try { ws.close(); } catch (err) { logger.debug("WS close failed:", err); } };
  }, [handleWsMsg, clearSender]);

  const send = async (md) => {
    if (!md || !md.trim() || !channel) return;
    sendTyping(false);
    try {
      await api.post("/chat/messages", { channel_id: channel.id, text: md });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!channel) {
    return (
      <div className={`${embedded ? "p-5" : "card-flat mt-6 p-5"} text-xs text-slate-500 flex items-center gap-2`} data-testid="embedded-chat-empty">
        <MessageSquare size={14} className="text-slate-400" /> Project chat will appear here once your project lead opens the room.
      </div>
    );
  }

  const wrapperClass = embedded
    ? "flex-1 min-h-0 flex flex-col"
    : "card-flat mt-6 flex flex-col";
  const wrapperStyle = embedded ? undefined : { height: 440 };

  return (
    <div className={wrapperClass} style={wrapperStyle} data-testid="embedded-chat">
      {!embedded && (
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="min-w-0">
            <div className="label-overline">Project room</div>
            <div className="font-display font-bold text-sm text-slate-900 truncate">#{channel.name} · {projectName}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setSavedOpen(true)} className="text-[11px] text-slate-500 hover:text-[#F54800] inline-flex items-center gap-1" data-testid="embedded-chat-saved-open">
              <Bookmark size={12} /> Saved{savedIds.size > 0 ? ` (${savedIds.size})` : ""}
            </button>
            <Link to="/client/chat" className="text-[11px] text-[#F54800] hover:underline inline-flex items-center gap-0.5">
              Open full chat <ChevronRight size={12} />
            </Link>
          </div>
        </div>
      )}
      {embedded && (
        <div className="px-4 pt-2 flex justify-end">
          <button onClick={() => setSavedOpen(true)} className="text-[11px] text-slate-500 hover:text-[#F54800] inline-flex items-center gap-1" data-testid="embedded-chat-saved-open">
            <Bookmark size={12} /> Saved{savedIds.size > 0 ? ` (${savedIds.size})` : ""}
          </button>
        </div>
      )}
      {messages.some((m) => m.pinned) && (
        <div className="px-4 py-1.5 bg-[#FFF8F3] border-b border-[#F54800]/20 space-y-1 max-h-24 overflow-y-auto" data-testid="client-pinned-panel">
          {messages.filter((m) => m.pinned).map((m) => (
            <button key={m.id} onClick={() => document.getElementById(`cmsg-${m.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })} className="w-full text-left text-[11px] text-slate-700 hover:text-[#F54800] truncate flex items-center gap-1.5" data-testid={`client-pinned-item-${m.id}`}>
              <Pin size={10} className="text-[#F54800] shrink-0" /> <span className="font-medium">{m.sender_name}:</span> {m.text || "📎 attachment"}
            </button>
          ))}
        </div>
      )}
      <div ref={listRef} className={`flex-1 overflow-y-auto px-${compact ? 4 : 5} py-3 space-y-2 bg-[#F8F9FA] min-h-0`} data-testid="embedded-chat-messages">
        {messages.length === 0 && <div className="text-center text-xs text-slate-400 mt-8">Say hello — the project team will see this.</div>}
        {messages.map((m, i) => {
          const mine = m.sender_id === user?.id;
          return (
            <React.Fragment key={m.id}>
              {(i === 0 || !sameDay(messages[i - 1].created_at, m.created_at)) && <ChatDateDivider date={m.created_at} messages={messages} idPrefix="cmsg" />}
            <div id={`cmsg-${m.id}`} className={`flex items-end gap-1 group ${mine ? "justify-end" : "justify-start"}`}>
              {mine && <SaveMsgButton mid={m.id} saved={savedIds.has(m.id)} onToggle={toggleSave} mine={false} />}
              <div className={`max-w-[80%] px-3 py-2 text-sm ${mine ? "bg-[#F54800] text-white chat-bubble-mine rounded-l-md rounded-tr-md" : "bg-white border border-slate-200 text-slate-900 rounded-r-md rounded-tl-md"}`}>
                {!mine && <div className="text-xs font-medium text-slate-500 mb-0.5">{m.sender_name}</div>}
                <div className="whitespace-pre-wrap break-words chat-body">{renderRich(m.text, m.mentions, user?.id)}</div>
                {m.created_at && <RelativeTime date={m.created_at} className={`block mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-slate-400"}`} testid={`cmsg-time-${m.id}`} />}
              </div>
              {!mine && <SaveMsgButton mid={m.id} saved={savedIds.has(m.id)} onToggle={toggleSave} mine={false} />}
            </div>
            </React.Fragment>
          );
        })}
      </div>
      <TypingLine typingUsers={typingUsers} testId="client-typing-indicator" />
      <div className="px-3 py-3 border-t border-slate-200 bg-white">
        <RichChatComposer onSend={send} onTyping={sendTyping} placeholder="Message project team…" testIdPrefix="embedded-chat" />
      </div>
      <ClientSavedModal open={savedOpen} onClose={() => setSavedOpen(false)} savedList={savedList} onJump={jumpToSaved} onRemove={toggleSave} />
    </div>
  );
}

function ClientChat() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const listRef = useRef(null);
  const activeRef = useRef(null);
  const wsRef = useRef(null);
  const { savedIds, savedList, toggleSave } = useChatSaved();
  const [savedOpen, setSavedOpen] = useState(false);
  const { typingUsers, setChannel: setTypingChannel, handleWsMsg, clearSender, sendTyping } = useChatTyping(wsRef, user?.id);
  useEffect(() => { activeRef.current = active; setTypingChannel(active?.id || null); }, [active, setTypingChannel]);
  const jumpToSaved = (item) => {
    setSavedOpen(false);
    setActive({ type: "channel", id: item.scope_id });
    setTimeout(() => document.getElementById(`cmsg-${item.message_id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 400);
  };

  useEffect(() => {
    api.get("/client/chat/channels")
      .then(({ data }) => {
        setChannels(data);
        if (data.length) setActive({ type: "channel", id: data[0].id });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get("/chat/messages", { params: { channel_id: active.id } })
      .then(({ data }) => {
        setMessages(data);
        setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
      })
      .catch(() => {});
  }, [active]);

  useEffect(() => {
    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (msg.type === "typing") { handleWsMsg(msg); return; }
      const cur = activeRef.current;
      if (cur && msg.channel_id === cur.id) {
        clearSender(msg.sender_id);
        setMessages((m) => {
          if (m.some((x) => x.id === msg.id)) return m.map((x) => (x.id === msg.id ? { ...x, ...msg } : x));
          setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
          return [...m, msg];
        });
      }
    };
    ws.onerror = (err) => logger.warn("Client WS error:", err);
    return () => {
      try { ws.close(); } catch (err) {
        logger.debug("Client WS close failed:", err);
      }
    };
  }, [handleWsMsg, clearSender]);

  const send = async (md) => {
    if (!md || !md.trim() || !active) return;
    sendTyping(false);
    try {
      await api.post("/chat/messages", { channel_id: active.id, text: md });
      // WebSocket echoes the message back; appending here too would duplicate it.
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="client-chat-page">
      <div className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="label-overline">Project rooms</div>
          <div className="font-display text-xl font-bold text-slate-900">Chat</div>
        </div>
        <button onClick={() => setSavedOpen(true)} className="w-full flex items-center gap-2 px-5 py-2.5 border-b border-slate-200 text-sm text-slate-700 hover:bg-slate-50" data-testid="client-chat-saved-open">
          <Bookmark size={15} className="text-[#F54800]" />
          <span className="font-medium">Saved</span>
          {savedIds.size > 0 && <span className="ml-auto text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-[#F54800]/10 text-[#F54800]">{savedIds.size}</span>}
        </button>
        <div className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
          {channels.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive({ type: "channel", id: c.id })}
              className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${active?.id === c.id ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"}`}
              data-testid={`client-channel-${c.id}`}
            >
              <Hash size={14} /> {c.name}
            </button>
          ))}
          {channels.length === 0 && <div className="px-3 py-4 text-xs text-slate-400">No project rooms yet.</div>}
        </div>
      </div>
      <div className="flex-1 flex flex-col bg-[#F8F9FA] min-w-0">
        <div className="px-6 py-4 bg-white border-b border-slate-200">
          <div className="label-overline">Project room</div>
          <div className="font-display text-lg font-bold text-slate-900">{channels.find((c) => c.id === active?.id)?.description || "Pick a project"}</div>
        </div>
        {messages.some((m) => m.pinned) && (
          <div className="px-6 py-1.5 bg-[#FFF8F3] border-b border-[#F54800]/20 space-y-1 max-h-24 overflow-y-auto" data-testid="client-pinned-panel">
            {messages.filter((m) => m.pinned).map((m) => (
              <button key={m.id} onClick={() => document.getElementById(`cmsg-${m.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })} className="w-full text-left text-[11px] text-slate-700 hover:text-[#F54800] truncate flex items-center gap-1.5" data-testid={`client-pinned-item-${m.id}`}>
                <Pin size={10} className="text-[#F54800] shrink-0" /> <span className="font-medium">{m.sender_name}:</span> {m.text || "📎 attachment"}
              </button>
            ))}
          </div>
        )}
        <div ref={listRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {messages.map((m, i) => {
            const mine = m.sender_id === user.id;
            return (
              <React.Fragment key={m.id}>
                {(i === 0 || !sameDay(messages[i - 1].created_at, m.created_at)) && <ChatDateDivider date={m.created_at} messages={messages} idPrefix="cmsg" />}
              <div id={`cmsg-${m.id}`} className={`flex items-end gap-1 group ${mine ? "justify-end" : "justify-start"}`}>
                {mine && <SaveMsgButton mid={m.id} saved={savedIds.has(m.id)} onToggle={toggleSave} mine={false} />}
                <div className={`max-w-[70%] px-3 py-2 text-sm ${mine ? "bg-[#F54800] text-white chat-bubble-mine rounded-l-md rounded-tr-md" : "bg-white border border-slate-200 text-slate-900 rounded-r-md rounded-tl-md"}`}>
                  {!mine && <div className="text-xs font-medium text-slate-500 mb-0.5">{m.sender_name}</div>}
                  <div className="whitespace-pre-wrap chat-body">{renderRich(m.text, m.mentions, user.id)}</div>
                  {m.created_at && <RelativeTime date={m.created_at} className={`block mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-slate-400"}`} testid={`cmsg-time-${m.id}`} />}
                </div>
                {!mine && <SaveMsgButton mid={m.id} saved={savedIds.has(m.id)} onToggle={toggleSave} mine={false} />}
              </div>
              </React.Fragment>
            );
          })}
          {messages.length === 0 && active && <div className="text-center text-sm text-slate-400 mt-10">Say hello — the project team will see this.</div>}
        </div>
        <TypingLine typingUsers={typingUsers} testId="client-typing-indicator" />
        {active && (
          <div className="px-6 py-4 pr-48 bg-white border-t border-slate-200 relative z-10">
            <RichChatComposer onSend={send} onTyping={sendTyping} placeholder="Message project team…" testIdPrefix="client-chat" />
          </div>
        )}
      </div>
      <ClientSavedModal open={savedOpen} onClose={() => setSavedOpen(false)} savedList={savedList} onJump={jumpToSaved} onRemove={toggleSave} />
    </div>
  );
}

export default function ClientPortal() {
  return (
    <ClientShell>
      <Routes>
        <Route index element={<ClientHome />} />
        <Route path="chat" element={<ClientChat />} />
        <Route path="vault" element={<ClientVault />} />
        <Route path="contracts" element={<ClientVault initialTab="contracts" />} />
        <Route path="invoices" element={<ClientVault initialTab="invoices" />} />
        <Route path="projects/:id" element={<ClientProject />} />
      </Routes>
    </ClientShell>
  );
}

function ClientVault({ initialTab = "contracts" }) {
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto fade-in" data-testid="client-vault-page">
      <div className="mb-6">
        <div className="label-overline text-[#F54800]">Studio portal</div>
        <h1 className="font-display text-4xl font-bold text-slate-900 tracking-tight">My Vault</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">All your paperwork, weekly reports and final deliverables — kept in one quiet place.</p>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-slate-200 overflow-x-auto" data-testid="vault-tabs">
        {[
          { id: "contracts",    label: "Contracts",    icon: FileSignature },
          { id: "invoices",     label: "Invoices",      icon: Receipt },
          { id: "reports",      label: "Reports",      icon: FileText },
          { id: "deliverables", label: "Deliverables", icon: Package2 },
        ].map((t) => {
          const Ico = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`vault-tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2 shrink-0 ${
                active ? "border-[#F54800] text-[#F54800]" : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <Ico size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "contracts"    && <VaultContracts />}
      {tab === "invoices"     && <VaultInvoices />}
      {tab === "reports"      && <VaultReports />}
      {tab === "deliverables" && <VaultDeliverables />}
    </div>
  );
}

function VaultTileGrid({ children, emptyText, testId }) {
  const items = React.Children.toArray(children);
  if (items.length === 0) {
    return (
      <div className="card-flat p-8 text-center text-sm text-slate-500" data-testid={`${testId}-empty`}>
        <FolderOpen size={28} className="mx-auto text-slate-300 mb-2" />
        {emptyText}
      </div>
    );
  }
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={testId}>{children}</div>;
}

function VaultTile({ icon: Icon, tone = "slate", title, subtitle, meta, actions, testId }) {
  return (
    <div className="card-flat p-5 flex flex-col gap-3 hover:shadow-sm transition-shadow" data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-10 h-10 rounded-sm flex items-center justify-center ${tone === "brand" ? "bg-[#F54800]/10 text-[#F54800]" : tone === "green" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
          <Icon size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-slate-900 truncate">{title}</div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{subtitle}</div>}
        </div>
      </div>
      {meta && <div className="text-[11px] text-slate-500 space-y-0.5">{meta}</div>}
      {actions && <div className="flex items-center gap-2 flex-wrap mt-1">{actions}</div>}
    </div>
  );
}

function VaultContracts() {
  const [rows, setRows] = useState([]);
  const [sigOpen, setSigOpen] = useState(false);
  const [sigTarget, setSigTarget] = useState(null);
  const load = () => api.get("/client/contracts").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openSign = (r) => { setSigTarget(r); setSigOpen(true); };
  const handleSign = async (payload) => {
    if (!sigTarget) return;
    try {
      await api.post(`/contracts/${sigTarget.id}/sign`, payload);
      toast.success("Signed");
      setSigOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <VaultTileGrid emptyText="No contracts yet." testId="vault-contracts">
        {rows.map((r) => {
          const ok = r.status === "approved";
          const signedAt = r.signature?.signed_at;
          return (
            <VaultTile
              key={r.id}
              icon={FileSignature}
              tone={ok ? "green" : "brand"}
              title={r.title}
              subtitle={r.notes || `${r.contract_type} · ${r.status}`}
              testId={`client-contract-${r.id}`}
              meta={
                <>
                  <div>Type · <b className="text-slate-700">{r.contract_type}</b></div>
                  <div>Status · <b className="text-slate-700">{r.status}</b></div>
                  <div>Created · {formatDate(r.created_at)}</div>
                  {signedAt && <div>Signed · <b className="text-green-700">{formatDate(signedAt)}</b></div>}
                </>
              }
              actions={
                <>
                  {r.link && <a href={r.link} target="_blank" rel="noreferrer" className="text-xs text-[#F54800] hover:underline">Open document →</a>}
                  {!ok && <Button size="sm" onClick={() => openSign(r)} className="rounded-sm h-7 bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid={`client-sign-${r.id}`}><FileSignature size={12} className="mr-1" /> Sign</Button>}
                  {ok && r.signature?.signature_url && <img src={r.signature.signature_url} alt="signature" className="h-6 border border-slate-200 rounded-sm bg-white" title={`Signed ${formatDate(signedAt)}`} />}
                  {ok && !r.signature?.signature_url && r.signature?.typed_text && <span className="text-sm italic" style={{ fontFamily: "'Brush Script MT', cursive" }}>{r.signature.typed_text}</span>}
                </>
              }
            />
          );
        })}
      </VaultTileGrid>
      <SignaturePad open={sigOpen} onClose={() => setSigOpen(false)} onSign={handleSign} signerNameDefault={sigTarget?.signer_name} />
    </>
  );
}

function VaultReports() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    api.get("/client/weekly-summaries", { params: { include_drafts: true } }).then((r) => setRows(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (filter === "sent") return rows.filter((r) => r.sent_at);
    if (filter === "draft") return rows.filter((r) => !r.sent_at);
    return rows;
  }, [rows, filter]);

  const counts = useMemo(() => ({
    all: rows.length,
    sent: rows.filter((r) => r.sent_at).length,
    draft: rows.filter((r) => !r.sent_at).length,
  }), [rows]);

  return (
    <>
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="vault-reports-filter">
        {[
          { id: "all",   label: "All" },
          { id: "sent",  label: "Sent" },
          { id: "draft", label: "Draft" },
        ].map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              data-testid={`reports-filter-${f.id}`}
              className={`px-3 py-1.5 text-xs rounded-sm border transition-colors inline-flex items-center gap-1.5 ${
                active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              {f.label} <span className={`text-[10px] ${active ? "opacity-80" : "opacity-50"}`}>{counts[f.id]}</span>
            </button>
          );
        })}
      </div>
      <VaultTileGrid emptyText={filter === "draft" ? "No drafts at the moment." : "No reports yet — your project lead will start sending weekly updates soon."} testId="vault-reports">
        {filtered.map((r) => (
          <VaultTile
            key={r.id}
            icon={FileText}
            tone={r.sent_at ? "brand" : "slate"}
            title={`Week of ${formatDate(r.week_start)}`}
            subtitle={r.project_name || r.summary?.slice(0, 90)}
            testId={`client-report-${r.id}`}
            meta={
              <>
                {r.project_name && <div>Project · <b className="text-slate-700">{r.project_name}</b></div>}
                <div>
                  {r.sent_at ? (
                    <>Sent · <b className="text-green-700">{formatDate(r.sent_at)}</b></>
                  ) : (
                    <span className="inline-flex items-center gap-1"><span className="px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider font-medium">Draft</span> Created {formatDate(r.created_at)}</span>
                  )}
                </div>
                {r.percent_complete != null && <div>Progress · <b className="text-slate-700">{Math.round(r.percent_complete)}%</b></div>}
              </>
            }
            actions={
              <div className="flex items-center justify-between gap-2">
                {r.summary && <p className="text-xs text-slate-600 line-clamp-3 flex-1">{r.summary}</p>}
                {r.sent_at && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/weekly-summary/${r.id}/pdf`}
                    target="_blank" rel="noreferrer"
                    className="text-xs text-[#F54800] hover:underline inline-flex items-center gap-1 shrink-0"
                    data-testid={`download-report-pdf-${r.id}`}
                  >
                    <Download size={12} /> PDF
                  </a>
                )}
              </div>
            }
          />
        ))}
      </VaultTileGrid>
    </>
  );
}

function VaultInvoices() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [markingPaid, setMarkingPaid] = useState(null);
  const load = () => api.get("/client/invoices").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const isPaid = (r) => r.status === "received" || r.status === "paid";
  // Note: the app's canonical invoice status enum is draft -> sent -> received
  // -> overdue -> cancelled ("paid" only lives on as a legacy alias — see
  // lib/finance.js `isReceivedStatus`). This tab's "Paid" label intentionally
  // matches both, to speak the client's language without diverging from the
  // real backend enum — don't "clean up" the 'paid' check out of this list.

  const filtered = useMemo(() => {
    if (filter === "paid") return rows.filter(isPaid);
    if (filter === "sent") return rows.filter((r) => !isPaid(r));
    return rows;
  }, [rows, filter]);

  const counts = useMemo(() => ({
    all: rows.length,
    sent: rows.filter((r) => !isPaid(r)).length,
    paid: rows.filter(isPaid).length,
  }), [rows]);

  const fmtAmount = (r) => `${r.currency === "INR" ? "₹" : r.currency + " "}${Number(r.amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  // Feb 2026 — Interactive "Mark as Paid": client self-reports a payment;
  // Finance still has the final say via the True Received Date flow.
  const markAsPaid = async (r) => {
    setMarkingPaid(r.id);
    try {
      await api.post(`/client/invoices/${r.id}/mark-paid`);
      toast.success("Marked as paid — the team will confirm shortly");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMarkingPaid(null); }
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="vault-invoices-filter">
        {[
          { id: "all",  label: "All" },
          { id: "sent", label: "Unpaid" },
          { id: "paid", label: "Paid" },
        ].map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              data-testid={`invoices-filter-${f.id}`}
              className={`px-3 py-1.5 text-xs rounded-sm border transition-colors inline-flex items-center gap-1.5 ${
                active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              {f.label} <span className={`text-[10px] ${active ? "opacity-80" : "opacity-50"}`}>{counts[f.id]}</span>
            </button>
          );
        })}
      </div>
      <VaultTileGrid emptyText="No invoices yet." testId="vault-invoices">
        {filtered.map((r) => (
          <VaultTile
            key={r.id}
            icon={Receipt}
            tone={isPaid(r) ? "green" : "brand"}
            title={r.invoice_number || "Invoice"}
            subtitle={r.project_name}
            testId={`client-invoice-${r.id}`}
            meta={
              <>
                {r.project_name && <div>Project · <b className="text-slate-700">{r.project_name}</b></div>}
                <div>Amount · <b className="text-slate-700">{fmtAmount(r)}</b></div>
                <div>Date · {formatDate(r.date)}</div>
                {r.due_date && <div>Due · {formatDate(r.due_date)}</div>}
                {r.payment_received_date && <div>Received · <b className="text-green-700">{formatDate(r.payment_received_date)}</b></div>}
                <div className="pt-0.5"><AccountabilityBadge inv={r} testId={`invoice-badge-${r.id}`} /></div>
              </>
            }
            actions={
              <>
                {r.file_url && (
                  <a href={r.file_url} target="_blank" rel="noreferrer" className="text-xs text-[#F54800] hover:underline inline-flex items-center gap-1" data-testid={`download-invoice-${r.id}`}>
                    <Download size={12} /> Download
                  </a>
                )}
                {!isPaid(r) && (
                  r.client_flagged_paid ? (
                    <span className="text-xs text-slate-400 italic inline-flex items-center gap-1" data-testid={`invoice-flagged-paid-${r.id}`}>
                      <CheckCircle2 size={12} /> Reported paid — pending confirmation
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => markAsPaid(r)}
                      disabled={markingPaid === r.id}
                      className="text-xs text-[#F54800] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`mark-as-paid-btn-${r.id}`}
                    >
                      <CheckCircle2 size={12} /> {markingPaid === r.id ? "Marking…" : "Mark as Paid"}
                    </button>
                  )
                )}
              </>
            }
          />
        ))}
      </VaultTileGrid>
    </>
  );
}

function VaultDeliverables() {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null); // { url, title }
  useEffect(() => {
    api.get("/client/deliverables").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  return (
    <>
      <VaultTileGrid emptyText="No deliverables uploaded yet. Final files and handoffs will land here." testId="vault-deliverables">
        {rows.map((d) => (
          <VaultTile
            key={d.id}
            icon={Package2}
            tone="green"
            title={d.title}
            subtitle={d.description || d.project_name}
            testId={`client-deliverable-${d.id}`}
            meta={
              <>
                {d.project_name && <div>Project · <b className="text-slate-700">{d.project_name}</b></div>}
                <div>Delivered · <b className="text-green-700">{formatDate(d.delivered_at)}</b></div>
                {d.file_name && <div>File · <span className="text-slate-700">{d.file_name}</span></div>}
              </>
            }
            actions={
              <>
                {d.google_drive_link && (
                  <button
                    onClick={() => setPreview({ url: d.google_drive_link, title: d.title })}
                    className="text-xs text-[#F54800] hover:underline inline-flex items-center gap-1"
                    data-testid={`preview-deliverable-${d.id}`}
                  >
                    <Eye size={12} /> Preview
                  </button>
                )}
                {d.file_url && (
                  <a href={d.file_url} target="_blank" rel="noreferrer" className="text-xs text-[#F54800] hover:underline inline-flex items-center gap-1" data-testid={`download-deliverable-${d.id}`}>
                    <Download size={12} /> Download
                  </a>
                )}
              </>
            }
          />
        ))}
      </VaultTileGrid>
      <DriveLinkPreviewModal open={!!preview} url={preview?.url} title={preview?.title} onClose={() => setPreview(null)} />
    </>
  );
}
